import { useState, useEffect, useCallback } from "react"
import {
  STORAGE_KEY,
  DEFAULT_TABLE_SETTINGS,
  SETTINGS_VERSION,
} from "../constants/settings"

export function useTableSettings() {
  // Load table settings from localStorage or use default
  const [tableSettings, setTableSettings] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        try {
          const parsed = JSON.parse(saved)

          if (!parsed.version || parsed.version !== SETTINGS_VERSION) {
            localStorage.removeItem(STORAGE_KEY)
            return DEFAULT_TABLE_SETTINGS
          }

          // Version matches - merge stored settings with defaults to handle any new tables
          const mergedOrder = [...(parsed.order || [])]
          const mergedExpanded = {
            ...DEFAULT_TABLE_SETTINGS.expanded,
            ...(parsed.expanded || {}),
          }

          // Add any new tables from defaults that aren't in stored order
          DEFAULT_TABLE_SETTINGS.order.forEach(tableKey => {
            if (!mergedOrder.includes(tableKey)) {
              mergedOrder.push(tableKey)
            }
          })

          return {
            version: SETTINGS_VERSION,
            order: mergedOrder,
            expanded: mergedExpanded,
          }
        } catch {
          localStorage.removeItem(STORAGE_KEY)
          return DEFAULT_TABLE_SETTINGS
        }
      }
    }
    return DEFAULT_TABLE_SETTINGS
  })

  // Extract order and expanded states for easier access
  const tableOrder = tableSettings.order
  const expandedStates = tableSettings.expanded

  // Update table order
  const setTableOrder = useCallback((newOrder: string[]) => {
    setTableSettings(prev => ({
      ...prev,
      version: SETTINGS_VERSION,
      order: newOrder,
    }))
  }, [])

  // Update expanded states
  const setExpandedStates = useCallback(
    (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => {
      setTableSettings(prev => ({
        ...prev,
        version: SETTINGS_VERSION,
        expanded: updater(prev.expanded),
      }))
    },
    [],
  )

  // Save to localStorage whenever settings change
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tableSettings))
    }
  }, [tableSettings])

  return {
    tableOrder,
    expandedStates,
    setTableOrder,
    setExpandedStates,
  }
}
